<template>
    <div>
        <div>
            <div class="headline">Incidents</div>
            <p class="my-4 caption" style="color: #C0C3CF; font-size: 14px;">
                Incidents allow users to collect information about student-specific events that can be imported from your SIS or created/entered by users in eduCLIMBER.
                Incident data can be instantly visualized in charts or linked into the thresholds for automated reporting allowing teams to easily monitor
                and analyze trends at the school-wide, small group, and student level.
            </p>
        </div>

        <div>
            <v-form v-model="valid">
                <div class="d-flex">
                    <div class="incident-label">Motivation Display Name</div>
                    <div>
                        <v-text-field
                            flat solo dense
                            v-model="keys.INCIDENT_MOTIVATION_LABEL"
                            @change="onFieldChange"
                            style="width: 200px;"
                            clearable
                        />
                    </div>
                </div>

                <div class="d-flex">
                    <div class="incident-label">Types per Incident Limit</div>

                    <div>
                        <v-text-field
                            required
                            flat solo dense
                            v-model="keys.INCIDENT_BEHAVIOR_CNT"
                            @change="onFieldChange"
                            type="number"
                            style="width: 100px;"
                            :rules="$fieldValidators('integer', 'Limit', { min: 1, max: 10 })"
                            clearable
                        />
                    </div>
                </div>

                <div class="d-flex">
                    <div class="incident-label">Incident Response Limit</div>
                    <div>
                        <v-text-field
                            required
                            flat solo dense
                            v-model="keys.INCIDENT_RESPONSE_CNT"
                            @change="onFieldChange"
                            type="number"
                            style="width: 100px;"
                            :rules="$fieldValidators('integer', 'Limit', { min: 1, max: 10 })"
                            clearable
                        />
                    </div>
                </div>

                <div class="d-flex" v-if="showIncidentBehaviorCounts">
                    <div class="incident-label mb-2" id="display-primary-counts-label">
                        Display counts by primary codes and responses
                        <fe-info-tip 
                            id="display-primary-counts-help"
                            tooltip="If off, each behavior and response will be counted individually when more than one behavior or response is linked to the same incident. If on, only primary behaviors and responses are counted." 
                            class="ml-2"
                        ></fe-info-tip>
                    </div>
                    <div>
                        <fe-switch 
                            v-model="keys.INCIDENT_COUNT_BY_PRIMARY" 
                            @change="onSwitchChange"
                            aria-labelledby="display-primary-counts-label"
                            aria-describedby="display-primary-counts-help"
                        ></fe-switch>
                    </div>
                </div>
            </v-form>
        </div>
    </div>
</template>

<script>
    import DetailChip from '@/components/common/DetailChip'

    export default {
        name: 'IncidentGeneral',
        components: {
            DetailChip
        },
        data() {
            return {
                valid: false,
                saving: false,
                saveQueued: false,
                preferences: {},
                keys: {
                    INCIDENT_MOTIVATION_LABEL: null,
                    INCIDENT_BEHAVIOR_CNT: null,
                    INCIDENT_RESPONSE_CNT: null,
                    INCIDENT_COUNT_BY_PRIMARY: null,
                }
            }
        },
        computed: {
            showIncidentBehaviorCounts() {
                return this.$store.getters['flags/flags']['incident-behavior-counts'] === true
            } 
        },
        mounted() {
            this.load()
            this.setSwitchDataTest()
        },
        updated() {
            this.setSwitchDataTest()
        },
        watch: {
            showIncidentBehaviorCounts() {
                this.setSwitchDataTest()
            }
        },
        methods: {
            setSwitchDataTest() {
                this.$nextTick(() => {
                    setTimeout(() => {
                        const labelElement = document.getElementById('display-primary-counts-label')
                        if (labelElement) {
                            const switchContainer = labelElement.closest('.d-flex')?.querySelector('.fe-switch-wrapper')
                            const slotElement = switchContainer?.querySelector('.v-input__slot')
                            if (slotElement && !slotElement.hasAttribute('data-test')) {
                                slotElement.setAttribute('data-test', 'incidents-display-primary-counts-toggle')
                            }
                        }
                    }, 100)
                })
            },
            doTest() {},
            load() {
                this.$axios.get('preferences.php?action=get&property=district')
                    .then(response => {
                        this.preferences = this.$ecResponse(response, 'preferences')

                        this.preferences.forEach(pref => {
                            Object.keys(this.keys).forEach(key => {
                                if (pref.code === key) {
                                    if (pref.code == 'INCIDENT_COUNT_BY_PRIMARY') this.keys[key] = pref.user_value == "1" ? true : false
                                    else this.keys[key] = pref.user_value
                                }
                            })
                        })
                    })
            },
            onFieldChange() {
                this.savePreferences()
            },
            // FeSwitch's own change() is the only thing that emits input, and a parent @change
            // listener replaces it, so v-model never updates. Take the value from the event.
            onSwitchChange(value) {
                this.keys.INCIDENT_COUNT_BY_PRIMARY = value
                this.savePreferences()
            },
            // Saves are serialized. Each save posts the whole form, so two in flight at once
            // let an older value overwrite a newer one. A change made while a save is in flight
            // marks another save as needed; it is built from the current keys once that one settles.
            savePreferences() {
                if (!this.valid) return

                if (this.saving) {
                    this.saveQueued = true
                    return
                }

                this.saving = true
                this.saveQueued = false

                let keys = this.$_.cloneDeep(this.keys)

                Object.keys(keys).forEach(key => {
                    if (key == 'INCIDENT_COUNT_BY_PRIMARY') keys[key] = this.keys[key] == true ? "1" : "0"
                    if (keys[key] === "") keys[key] = null
                })

                const settle = () => {
                    this.saving = false
                    if (this.saveQueued) this.savePreferences()
                }

                return this.$axios.post('preferences.php?action=update', keys)
                    .then(response => {
                        this.$ecResponse(response)
                        // Update districtConfig in store so components read the current value
                        if (keys.INCIDENT_COUNT_BY_PRIMARY !== undefined) {
                            const currentConfig = this.$store.state.global.districtConfig || {}
                            this.$store.commit('global/set', {
                                state: 'districtConfig',
                                value: { ...currentConfig, INCIDENT_COUNT_BY_PRIMARY: keys.INCIDENT_COUNT_BY_PRIMARY }
                            })
                        }
                    })
                    .then(settle, error => {
                        settle()
                        throw error
                    })
            }
        }
    }
</script>

<style lang="scss" scoped>
.incident-label {
    width: 275px !important;
    color: #050F2D;
    font-size: 14px;
    padding-top: 7px;
    display: flex;
}
</style>
